import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Circle, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// Custom red pin icon (SVG inline) to match brand and avoid default Leaflet icon issues
const createIcon = (primary: boolean) => {
  const color = primary ? '#C8102E' : '#1A1A1A';
  const size = primary ? 40 : 32;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
      <circle cx="12" cy="10" r="3" fill="white" stroke="none"/>
    </svg>
  `;
  return L.divIcon({
    html: svg,
    className: 'custom-leaflet-marker',
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size]
  });
};
export function ServiceAreas() {
  const areas = [{
    name: 'Monterey',
    status: 'Primary Service Area',
    primary: true,
    coords: [36.6002, -121.8947] as [number, number]
  }, {
    name: 'Salinas',
    status: 'Covered',
    coords: [36.6777, -121.6555] as [number, number]
  }, {
    name: 'Carmel',
    status: 'Covered',
    coords: [36.5552, -121.9233] as [number, number]
  }, {
    name: 'Pacific Grove',
    status: 'Covered',
    coords: [36.6177, -121.9166] as [number, number]
  }, {
    name: 'Seaside',
    status: 'Covered',
    coords: [36.6111, -121.8516] as [number, number]
  }, {
    name: 'Marina',
    status: 'Covered',
    coords: [36.6844, -121.8022] as [number, number]
  }, {
    name: 'Watsonville',
    status: 'Covered',
    coords: [36.9102, -121.7569] as [number, number]
  }, {
    name: 'Santa Cruz',
    status: 'Within Range',
    coords: [36.9741, -122.0308] as [number, number]
  }, {
    name: 'Surrounding Areas',
    status: 'Up to 50 Miles'
  }];
  const monterey: [number, number] = [36.6002, -121.8947];
  // 50 miles in meters
  const radiusMeters = 50 * 1609.34;
  const mappedAreas = areas.filter(a => a.coords);
  return <section id="areas" className="py-20 bg-[#1A1A1A] text-white" data-id="element-456" data-mp="1pp464z4wqjkd">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-457" data-mp="s36h0iw0vfzg">
        <div className="text-center mb-16" data-id="element-458" data-mp="x180h4xd1hbg">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold mb-4" data-id="element-459" data-mp="x1zwgguvdh29">
            WE COVER ALL OF{' '}
            <span className="text-[#C8102E]" data-id="element-460" data-mp="eagvefkeod2v">MONTEREY COUNTY</span> AND BEYOND
          </motion.h2>
          <p className="text-xl text-gray-400" data-id="element-461" data-mp="2rkqcdh8b2k09">
            Based in Monterey, CA — Serving a 50-mile radius.
          </p>
        </div>

        {/* Map + Areas Grid */}
        <div className="grid lg:grid-cols-5 gap-8 mb-12" data-id="element-462" data-mp="2nhall1ix3dvh">
          {/* Map */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="lg:col-span-3 rounded-xl overflow-hidden border-2 border-[#C8102E]/40 shadow-2xl relative bg-white/5 h-[400px] lg:h-auto lg:min-h-[500px]" data-id="element-463" data-mp="2iih3evsoqyoz">
            <MapContainer center={monterey} zoom={9} scrollWheelZoom={false} style={{
            height: '100%',
            width: '100%',
            minHeight: '400px',
            background: '#1A1A1A'
          }} className="z-0" data-id="element-464" data-mp="1uupu812fz1rx">
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" data-id="element-465" data-mp="3l044x3la4zvj" />

              {/* 50-mile coverage circle */}
              <Circle center={monterey} radius={radiusMeters} pathOptions={{
              color: '#C8102E',
              fillColor: '#C8102E',
              fillOpacity: 0.12,
              weight: 2,
              dashArray: '6, 6'
            }} data-id="element-466" data-mp="3g7kuuglswmvh" />

              {/* Pins */}
              {mappedAreas.map(area => <Marker key={area.name} position={area.coords as [number, number]} icon={createIcon(!!area.primary)} data-id="element-467" data-mp="2xa9suo8sbfcc">
                  <Tooltip direction="top" offset={[0, -30]} opacity={1} permanent={!!area.primary} data-id="element-468" data-mp="vi3qxpxntwbc">
                    <span className="font-bold" data-id="element-469" data-mp="2b1kwlq3vbvcv">{area.name}</span>
                  </Tooltip>
                  <Popup data-id="element-470" data-mp="2pq7i69k39jcj">
                    <div className="text-center" data-id="element-471" data-mp="2pqhc28898ani">
                      <strong data-id="element-472" data-mp="2kyz8dceujvyu">{area.name}</strong>
                      <br data-id="element-473" data-mp="2ky7hgew72mew" />
                      <span style={{
                    color: area.primary ? '#C8102E' : '#555'
                  }} data-id="element-474" data-mp="2kwweghakpiou">
                        {area.status}
                      </span>
                    </div>
                  </Popup>
                </Marker>)}
            </MapContainer>

            {/* Legend overlay */}
            <div className="absolute bottom-4 left-4 z-[400] bg-[#1A1A1A]/90 backdrop-blur-sm border border-white/10 rounded-lg p-3 text-xs space-y-2 pointer-events-none" data-id="element-475" data-mp="33ne4ge40u06i">
              <div className="flex items-center gap-2" data-id="element-476" data-mp="19d0lhpkzu12o">
                <span className="w-3 h-3 rounded-full bg-[#C8102E]" data-id="element-477" data-mp="39e8l7ayqll4y"></span>
                <span className="text-white font-semibold" data-id="element-478" data-mp="39g1uky0qijgn">Primary Hub</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-479" data-mp="3eemusxfzl353">
                <span className="w-3 h-3 rounded-full bg-[#1A1A1A] border border-white" data-id="element-480" data-mp="3idlw882iehrs"></span>
                <span className="text-white font-semibold" data-id="element-481" data-mp="3nejai9keero0">Covered City</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-482" data-mp="3sbi7r0zczeqf">
                <span className="w-3 h-[2px] border-t-2 border-dashed border-[#C8102E]" data-id="element-483" data-mp="16a1hnusl0ka"></span>
                <span className="text-white font-semibold" data-id="element-484" data-mp="13xcwruj3ry9">50-Mile Radius</span>
              </div>
            </div>
          </motion.div>

          {/* Areas List */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 content-start" data-id="element-485" data-mp="9y245cdkr5eb">
            {areas.map((area, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.05
          }} className={`flex items-center justify-between p-4 rounded-lg border ${area.primary ? 'bg-[#C8102E]/20 border-[#C8102E]' : 'bg-white/5 border-white/10'}`} data-id="element-486" data-mp="jw87lyf14jla" data-mpi={idx}>
                <div className="flex items-center gap-3" data-id="element-487" data-mp="17rl4dld2otgb">
                  <MapPin size={20} className={area.primary ? 'text-[#C8102E]' : 'text-gray-400'} data-id="element-488" data-mp="17u63i2lzkvad" />
                  <span className="font-bold text-lg" data-id="element-489" data-mp="yxyjwrebfcmf">{area.name}</span>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${area.primary ? 'bg-[#C8102E] text-white' : 'bg-white/10 text-gray-300'}`} data-id="element-490" data-mp="1vjs50wptzmlj">
                  {area.status}
                </span>
              </motion.div>)}
          </div>
        </div>

        <div className="text-center bg-white/5 p-8 rounded-xl border border-white/10 inline-block w-full" data-id="element-491" data-mp="20h1w6hur94k8">
          <p className="text-xl mb-4" data-id="element-492" data-mp="25fd6etoyph2z">Not sure if you're in our range?</p>
          <a href="tel:8312407401" className="inline-flex items-center gap-2 text-[#C8102E] font-bold text-2xl hover:text-red-400 transition-colors" data-id="element-493" data-mp="25djwn5j1ygqr">
            <Phone size={24} data-id="element-494" data-mp="2y2hfb9r6efk1" />
            Just call. We'll tell you immediately.
          </a>
        </div>
      </div>
    </section>;
}